import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../core/services/auth.service';
import { UtilsService } from '../../core/services/utils.service';
import { AuthLoginResponseModel } from '../models/auth-login-response-model';
import { AuthLoginModel } from '../models/auth-login.model';
import { ReCaptchaPublicKey } from '../models/recaptcha-public-key.model';
import { AuthState } from './auth.state';

@Injectable({
	providedIn: 'root',
})
export class AuthApiService {
	constructor(
		private authService: AuthService,
		private authState: AuthState,
		private httpClient: HttpClient,
		private utilsService: UtilsService
	) {}

	login$(data: AuthLoginModel): Observable<AuthLoginResponseModel> {
		return this.httpClient.post<AuthLoginResponseModel>(this.getLoginPath(), data).pipe(
			tap((authResponseModel) => {
				this.authService.setTokens(authResponseModel.token, authResponseModel.refreshToken);
			}),
			catchError((response: HttpErrorResponse) => {
				this.authState.setError(this.utilsService.buildErrorModel(response));

				return throwError(response);
			})
		);
	}

	refreshToken$(): Observable<AuthLoginResponseModel> {
		return this.httpClient
			.post<AuthLoginResponseModel>(this.getRefreshTokenPath(), this.authService.getRefreshTokenData())
			.pipe(
				tap((authResponseModel) => {
					this.authService.setTokens(authResponseModel.token, authResponseModel.refreshToken);
				}),
				catchError((response: HttpErrorResponse) => {
					this.authState.setError(this.utilsService.buildErrorModel(response));

					return throwError(response);
				})
			);
	}

	getRecaptchaKey$(): Observable<string> {
		return this.httpClient.get<ReCaptchaPublicKey>(this.getRecaptchaKeyPath()).pipe(
			map((response: ReCaptchaPublicKey) => {
				return response.publicKey;
			})
		);
	}

	setNewPasword$(newPassword: { oldPassword: string; newPassword: string }): Observable<AuthLoginResponseModel> {
		return this.httpClient
			.post<AuthLoginResponseModel>(this.getSetNewPasswordPath(), this.authService.buildNewPasswordModel(newPassword))
			.pipe(
				tap((response) => {
					this.authService.setTokens(response.token, response.refreshToken);
				}),
				catchError((response: HttpErrorResponse) => {
					this.authState.setError(this.utilsService.buildErrorModel(response));

					return throwError(response);
				})
			);
	}

	forgottenPassword$(email: string): Observable<null> {
		return this.httpClient.post<null>(this.getForgottenPasswordPath(), { email: email }).pipe(
			catchError((response: HttpErrorResponse) => {
				this.authState.setError(this.utilsService.buildErrorModel(response));

				return throwError(response);
			})
		);
	}

	resetPassword$(newPassword: { newPassword: string; confirmPassword: string }): Observable<null> {
		return this.httpClient
			.post<null>(this.getResetPasswordPath(), this.authService.buildResetPasswordModel(newPassword))
			.pipe(
				catchError((response: HttpErrorResponse) => {
					this.authState.setError(this.utilsService.buildErrorModel(response));

					return throwError(response);
				})
			);
	}

	getLoginPath(): string {
		return `${this.getBasePath()}/sign-in-vendor`;
	}

	private getForgottenPasswordPath(): string {
		return `${this.getBasePath()}/forgotten-password`;
	}

	private getResetPasswordPath(): string {
		return `${this.getBasePath()}/reset-password`;
	}

	private getSetNewPasswordPath(): string {
		return `${this.getBasePath()}/set-new-password`;
	}

	private getRefreshTokenPath(): string {
		return `${this.getBasePath()}/refresh`;
	}

	private getRecaptchaKeyPath(): string {
		return `${environment.apiUrl}/Config/login-info`;
	}

	private getBasePath(): string {
		return `${environment.apiUrl}/Auth`;
	}
}
